import React from 'react';
import {
    Paper,
    Box,
    Typography,
    IconButton,
    Checkbox,
    Chip,
    useTheme,
    alpha,
    useMediaQuery
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { Material } from '../../types';

interface MaterialCardProps {
    material: Material;
    onToggleComprado: (material: Material) => void;
    onEdit: (material: Material) => void;
    onDelete: (material: Material) => void;
    puedeEditar: boolean;
    showObra: boolean;
    onViewPhoto?: (url: string) => void;
}

export const MaterialCard: React.FC<MaterialCardProps> = ({
    material,
    onToggleComprado,
    onEdit,
    onDelete,
    puedeEditar,
    showObra,
    onViewPhoto
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Paper
            elevation={0}
            sx={{
                p: isMobile ? 1.5 : 2,
                mb: isMobile ? 1 : 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'grey.200',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                    transform: 'translateY(-2px)'
                },
                display: 'flex',
                flexDirection: 'column',
                gap: isMobile ? 0.5 : 1.5,
                bgcolor: 'white'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 1 : 1.5, flex: 1 }}>
                    <Checkbox
                        checked={material.comprado}
                        onChange={() => onToggleComprado(material)}
                        disabled={!puedeEditar}
                        sx={{
                            p: 0,
                            '&.Mui-checked': {
                                color: 'success.main',
                            },
                        }}
                    />
                    <Box>
                        <Typography
                            variant={isMobile ? "body1" : "subtitle1"}
                            sx={{
                                fontWeight: 600,
                                lineHeight: isMobile ? 1.2 : 1.3,
                                textDecoration: material.comprado ? 'line-through' : 'none',
                                color: material.comprado ? 'text.secondary' : 'text.primary',
                                fontSize: isMobile ? '0.95rem' : undefined
                            }}
                        >
                            {material.nombre}
                        </Typography>
                        {showObra && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: isMobile ? 0.25 : 0.5 }}>
                                <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">
                                    {material.obraNombre}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: isMobile ? 0.25 : 0.5 }}>
                            <PersonIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.70rem' }}>
                                {material.usuarioNombre || material.usuarioEmail || 'Usuario descon.'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Chip
                    label={material.cantidad}
                    size="small"
                    color={material.comprado ? "default" : "primary"}
                    variant={material.comprado ? "outlined" : "filled"}
                    sx={{ fontWeight: 600, minWidth: 40, height: isMobile ? 24 : undefined }}
                />
            </Box>

            {puedeEditar && (
                <>
                    <Box sx={{ height: 1, bgcolor: 'grey.100', width: '100%', my: isMobile ? 0.5 : 0 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        {material.esFoto && material.fotoUrl && onViewPhoto && (
                            <IconButton
                                size="small"
                                onClick={() => onViewPhoto(material.fotoUrl!)}
                                sx={{
                                    color: 'info.main',
                                    bgcolor: alpha(theme.palette.info.main, 0.05),
                                    '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.1) },
                                    p: isMobile ? 0.5 : undefined
                                }}
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        )}
                        <IconButton
                            size="small"
                            onClick={() => onEdit(material)}
                            sx={{
                                color: 'primary.main',
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) },
                                p: isMobile ? 0.5 : undefined
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={() => onDelete(material)}
                            sx={{
                                color: 'error.main',
                                bgcolor: alpha(theme.palette.error.main, 0.05),
                                '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.1) },
                                p: isMobile ? 0.5 : undefined
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </>
            )}
        </Paper>
    );
};
